import React, { useState, useEffect, useMemo } from "react";
import { useParams, NavLink } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import { getBlogPostPath } from "../utils/blogRoutes";
import { robustFetch } from "../utils/apiUtils";
import "./ContinueReading.css";

export default function ContinueReading({ currentPostCategorySlug }) {
  const params = useParams();  
  let currentSlug = params.slug || Object.values(params).join();

  const [mongoPosts, setMongoPosts] = useState([]);
  const [mongoLoading, setMongoLoading] = useState(true);
  const [mongoError, setMongoError] = useState(null);

  useEffect(() => {
    const fetchMongoPosts = async () => {
      try {
        setMongoLoading(true);
        setMongoError(null);
        
        const response = await robustFetch(API_ENDPOINTS.BLOG.POSTS);
        
        if (!response.ok) {
          if (response.status === 404 || response.status === 503) {
            setMongoPosts([]);
            setMongoLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const postsData = await response.json();
        
        if (postsData && Array.isArray(postsData)) {
          const publishedPosts = postsData.filter(post => post.isPublished === true);
          
          if (publishedPosts.length > 0) {
            const formattedPosts = publishedPosts.map(post => {
              const category = post.category || {};
              // Match logic in Post.tsx to determine category slug
              const categorySlug = (category && category.slug) 
                  ? category.slug 
                  : (typeof category === 'object' && category.name 
                      ? category.name.toLowerCase().replace(/\s+/g, '-') 
                      : 'uncategorized');

              return {
                node: {
                  id: post._id || post.id,
                  slug: post.slug,
                  title: post.title,
                  description: post.description || "",
                  coverImage: post.coverImage,
                  postNumber: post.postNumber || 0,
                  createdAt: post.publishedAt || post.createdAt,
                  publishedAt: post.publishedAt,
                  categorySlug: categorySlug
                }
              };
            });
            
            setMongoPosts(formattedPosts);
          } else {
            setMongoPosts([]);
          }
        } else {
          setMongoPosts([]);
        }
        setMongoLoading(false);
      } catch (err) {
        console.error('Error fetching MongoDB posts:', err);
        setMongoError(err);
        setMongoPosts([]);
        setMongoLoading(false);
      }
    };

    fetchMongoPosts();
  }, []);

  const relatedPosts = useMemo(() => {
    if (!currentPostCategorySlug) return [];
    
    // Filter posts from same category, exclude current post
    const filtered = mongoPosts.filter(post => {
      if (!post.node) return false;
      return post.node.categorySlug === currentPostCategorySlug && post.node.slug !== currentSlug;
    });
    
    // Sort by publishedAt or createdAt descending
    filtered.sort((a, b) => {
      const dateA = new Date(a.node && (a.node.publishedAt || a.node.createdAt) ? (a.node.publishedAt || a.node.createdAt) : 0);
      const dateB = new Date(b.node && (b.node.publishedAt || b.node.createdAt) ? (b.node.publishedAt || b.node.createdAt) : 0);
      return dateB.getTime() - dateA.getTime();
    });

    return filtered.slice(0, 2);
  }, [mongoPosts, currentPostCategorySlug, currentSlug]);

  if (mongoLoading || relatedPosts.length === 0) {
    return null; // Return null while loading or if no posts match
  }

  return (
    <div className="continue-reading-container mt-4 mb-8">
      <div className="text-2xl font-bold text-[var(--text-color)] mb-6 tracking-tight">
        Continue Reading
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-8 w-full">
        {relatedPosts.map((post) => {
          const rawCoverUrl = (post.node.coverImage && post.node.coverImage.url) || "";
          const defaultThumbnail = "https://blog-post-details.s3.eu-north-1.amazonaws.com/Blog+Default+Thumbnail.jpg";
          const coverImageUrl = rawCoverUrl 
              ? (rawCoverUrl.startsWith('http') ? rawCoverUrl : `https://www.harivishnu.in${rawCoverUrl.startsWith('/') ? '' : '/'}${rawCoverUrl}`) 
              : defaultThumbnail;

          return (
            <NavLink
              key={post.node.id}
              to={getBlogPostPath(post.node)}
              className="group flex flex-col items-start w-full no-underline"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              }}
            >
              {/* Thumbnail */}
              <div className="w-full aspect-[16/9] mb-4 overflow-hidden rounded-xl border border-solid border-[var(--slider-color)]">
                <img 
                  src={coverImageUrl} 
                  alt={post.node.title} 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              {/* Title */}
              <div className="text-[0.95rem] md:text-[1.2rem] font-bold text-[var(--text-color)] leading-snug mb-2 line-clamp-2 transition-colors duration-200 group-hover:text-[#47b5ff]">
                {post.node.title}
              </div>

              {/* Description */}
              <p className="text-[0.8rem] md:text-[0.95rem] text-[#8f8f8f] leading-relaxed line-clamp-2 md:line-clamp-3 m-0 mb-4 w-full">
                {post.node.description}
              </p>

              {/* Read More Button */}
              <div className="w-full flex justify-center mt-auto">
                <span className="bg-[#47b5ff] text-white px-4 md:px-6 py-2 md:py-2.5 rounded-full font-semibold text-[0.8rem] md:text-[0.9rem] transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg text-center">
                  Read More
                </span>
              </div>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
